import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, Brain, AlertTriangle, TrendingUp, X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

type BannerVariant = "empty" | "unanalyzed" | "healthy" | "high-risk" | null;

interface BannerConfig {
  icon: typeof Upload;
  title: string;
  description: string;
  action: string;
  actionPath: string;
  gradient: string;
}

const bannerConfigs: Record<Exclude<BannerVariant, null>, BannerConfig> = {
  empty: {
    icon: Upload,
    title: "Unlock AI-Powered Compliance",
    description: "Upload your first environmental document to see predictive risk analysis, compliance scoring, and proactive recommendations.",
    action: "Upload Document",
    actionPath: "/documents",
    gradient: "from-primary/10 via-primary/5 to-transparent",
  },
  unanalyzed: {
    icon: Brain,
    title: "Documents Awaiting Analysis",
    description: "You have unanalyzed documents. Run AI analysis to unlock compliance insights and risk scoring.",
    action: "View Documents",
    actionPath: "/documents",
    gradient: "from-info/10 via-info/5 to-transparent",
  },
  healthy: {
    icon: TrendingUp,
    title: "Compliance Up to Date",
    description: "Your document vault is in good shape. Upload newer reports to keep your assessment current.",
    action: "Upload New Files",
    actionPath: "/documents",
    gradient: "from-success/10 via-success/5 to-transparent",
  },
  "high-risk": {
    icon: AlertTriangle,
    title: "Critical Findings Detected",
    description: "High-risk compliance flags have been detected. Review recommended actions immediately.",
    action: "Review Insights",
    actionPath: "/insights",
    gradient: "from-destructive/10 via-destructive/5 to-transparent",
  },
};

export default function ProactiveInsightsBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [variant, setVariant] = useState<BannerVariant>(null);
  const [dismissed, setDismissed] = useState(false);
  const [docCount, setDocCount] = useState(0);

  const checkState = useCallback(async () => {
    if (!user) return;
    const dismissedAt = localStorage.getItem(`banner-dismissed-${user.id}`);
    if (dismissedAt && Date.now() - parseInt(dismissedAt) < 24 * 60 * 60 * 1000) {
      setDismissed(true);
      return;
    }

    const { data: docs } = await supabase
      .from("documents")
      .select("status, risk_score")
      .eq("user_id", user.id);

    if (!docs || docs.length === 0) {
      setVariant("empty");
      return;
    }

    setDocCount(docs.length);
    const unanalyzed = docs.filter(d => d.status === "uploaded").length;
    const highRisk = docs.filter(d => (d.risk_score ?? 0) > 60).length;

    if (highRisk > 0) {
      setVariant("high-risk");
    } else if (unanalyzed > 0) {
      setVariant("unanalyzed");
    } else {
      setVariant("healthy");
    }
  }, [user]);

  useEffect(() => { checkState(); }, [checkState]);

  const handleDismiss = () => {
    setDismissed(true);
    if (user) localStorage.setItem(`banner-dismissed-${user.id}`, String(Date.now()));
  };

  if (!variant || dismissed) return null;

  const config = bannerConfigs[variant];
  const Icon = config.icon;

  return (
    <div className={`relative overflow-hidden rounded-xl border border-border/50 bg-gradient-to-r ${config.gradient} p-3 md:p-4 animate-fade-in-up`}>
      <div className="flex items-center gap-3 md:gap-4">
        <div className="h-8 w-8 md:h-10 md:w-10 rounded-lg md:rounded-xl bg-card/80 border border-border/50 flex items-center justify-center shrink-0">
          <Icon className="h-4 w-4 md:h-5 md:w-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs md:text-sm font-semibold">{config.title}</p>
          <p className="text-xs text-muted-foreground mt-0.5 hidden sm:block">
            {variant === "unanalyzed"
              ? config.description.replace("unanalyzed documents", `${docCount} unanalyzed documents`)
              : config.description}
          </p>
        </div>
        <div className="flex items-center gap-1.5 md:gap-2 shrink-0">
          <Button size="sm" className="text-[10px] md:text-xs h-7 md:h-8 px-2.5 md:px-3" onClick={() => navigate(config.actionPath)}>
            {config.action} <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
          <Button variant="ghost" size="icon" className="h-6 w-6 md:h-7 md:w-7" onClick={handleDismiss} aria-label="Dismiss insight">
            <X className="h-3 w-3 md:h-3.5 md:w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
